import React, { useState } from 'react';
import { Lock, Save, Eye, EyeOff } from 'lucide-react';
import PasswordStrengthIndicator from '../../shared/PasswordStrengthIndicator';
import { validatePasswordForm } from '../../../utils/passwordValidation';

const PasswordSettings = ({ formData, setFormData, onSave, isLoading = false }) => {
 const [showCurrentPassword, setShowCurrentPassword] = useState(false);
 const [showNewPassword, setShowNewPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 const [errors, setErrors] = useState({});

 const handleInputChange = (field, value) => {
 setFormData({ ...formData, [field]: value });
 
 // Clear field-specific errors when user starts typing
 if (errors[field]) {
 setErrors(prev => {
 const newErrors = { ...prev };
 delete newErrors[field];
 return newErrors;
 });
 }
 };

 const handleSave = () => {
 // Validate password form
 const validation = validatePasswordForm(formData);
 
 if (!validation.isValid) {
 setErrors(validation.errors);
 return;
 }
 
 // Clear errors and call onSave
 setErrors({});
 onSave();
 };

 return (
 <div className="product-panel p-4 sm:p-5 md:p-6">
 <div className="flex items-center gap-2.5 mb-4">
 <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-product-md border border-border bg-surface-muted">
 <Lock className="w-4 h-4 text-ink" />
 </div>
 <h2 className="text-lg sm:text-xl font-bold text-ink">Password</h2>
 </div>
 <div className="space-y-3 sm:space-y-4">
 <div>
 <label className="block text-xs sm:text-sm font-medium text-ink mb-1.5 sm:mb-2">
 Current Password
 </label>
 <div className="relative">
 <input
 type={showCurrentPassword ? 'text' : 'password'}
 value={formData.currentPassword || ''}
 onChange={(e) => handleInputChange('currentPassword', e.target.value)}
 className={`w-full px-3 py-2 sm:px-4 sm:py-2.5 pr-10 text-sm sm:text-base border bg-surface text-ink rounded-md focus:outline-none focus:ring-2 ${ errors.currentPassword ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-border focus:ring-ink focus:border-ink' }`}
 />
 <button
 type="button"
 onClick={() => setShowCurrentPassword(!showCurrentPassword)}
 className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-tertiary hover:text-ink"
 >
 {showCurrentPassword ? (
 <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
 ) : (
 <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
 )}
 </button>
 </div>
 {errors.currentPassword && (
 <p className="mt-1 text-xs sm:text-sm text-red-600">
 {typeof errors.currentPassword === 'string' ? errors.currentPassword : errors.currentPassword[0]}
 </p>
 )}
 </div>
 
 <div>
 <label className="block text-xs sm:text-sm font-medium text-ink mb-1.5 sm:mb-2">
 New Password
 </label>
 <div className="relative">
 <input
 type={showNewPassword ? 'text' : 'password'}
 value={formData.newPassword || ''}
 onChange={(e) => handleInputChange('newPassword', e.target.value)}
 className={`w-full px-3 py-2 sm:px-4 sm:py-2.5 pr-10 text-sm sm:text-base border bg-surface text-ink rounded-md focus:outline-none focus:ring-2 ${ errors.newPassword ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-border focus:ring-ink focus:border-ink' }`}
 />
 <button
 type="button"
 onClick={() => setShowNewPassword(!showNewPassword)}
 className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-tertiary hover:text-ink"
 >
 {showNewPassword ? (
 <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
 ) : (
 <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
 )}
 </button>
 </div>
 {errors.newPassword && (
 <p className="mt-1 text-xs sm:text-sm text-red-600">
 {typeof errors.newPassword === 'string' ? errors.newPassword : errors.newPassword[0]}
 </p>
 )}
 {/* Password Strength Indicator */}
 <PasswordStrengthIndicator password={formData.newPassword || ''} className="mt-2" />
 </div>
 
 <div>
 <label className="block text-xs sm:text-sm font-medium text-ink mb-1.5 sm:mb-2">
 Confirm New Password
 </label>
 <div className="relative">
 <input
 type={showConfirmPassword ? 'text' : 'password'}
 value={formData.confirmPassword || ''}
 onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
 className={`w-full px-3 py-2 sm:px-4 sm:py-2.5 pr-10 text-sm sm:text-base border bg-surface text-ink rounded-md focus:outline-none focus:ring-2 ${ errors.confirmPassword ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-border focus:ring-ink focus:border-ink' }`}
 />
 <button
 type="button"
 onClick={() => setShowConfirmPassword(!showConfirmPassword)}
 className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-tertiary hover:text-ink"
 >
 {showConfirmPassword ? (
 <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
 ) : (
 <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
 )}
 </button>
 </div>
 {errors.confirmPassword && (
 <p className="mt-1 text-xs sm:text-sm text-red-600">
 {typeof errors.confirmPassword === 'string' ? errors.confirmPassword : errors.confirmPassword[0]}
 </p>
 )}
 </div>
 
 <button
 type="button"
 onClick={handleSave}
 disabled={isLoading}
 className="flex items-center justify-center gap-2 rounded-product-md bg-ink px-4 py-2.5 text-white hover:bg-black sm:px-6 sm:py-3 transition-colors w-full sm:w-auto text-sm sm:text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
 >
 <Save className="w-4 h-4" />
 <span>{isLoading ? 'Updating...' : 'Update Password'}</span>
 </button>
 </div>
 </div>
 );
};

export default PasswordSettings;
